import React, { useEffect } from 'react';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, ShoppingCart, X } from 'lucide-react';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AudioPlayer = () => {
  const {
    currentTrack,
    isPlaying,
    progress,
    duration,
    volume,
    audioRef,
    togglePlay,
    seek,
    closePlayer,
    setProgress,
    setDuration,
    setVolume,
    setIsPlaying
  } = useAudioPlayer();

  const navigate = useNavigate();

  useEffect(() => {
    if (!audioRef.current || !currentTrack) return;
    // Always stream through the backend endpoint; the track.audio_url is just the storage path.
    const streamUrl = `${BACKEND_URL}/api/tracks/${currentTrack.id}/stream`;
    audioRef.current.src = streamUrl;
    audioRef.current.play().catch((err) => {
      console.error('Audio playback failed:', err);
      if (err?.name !== 'AbortError') {
        toast.error('Audio not available for this track yet.');
        setIsPlaying(false);
      }
    });
  }, [currentTrack, audioRef, setIsPlaying]);

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setProgress(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration || 180);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setProgress(0);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!currentTrack) {
    return null;
  }

  return (
    <>
      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />
      
      <div className="fixed bottom-0 left-0 right-0 z-[100] bg-slate-900/95 backdrop-blur-2xl border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-20 gap-4">
            {/* Track Info */}
            <div className="flex items-center gap-3 min-w-0 flex-1 md:flex-initial md:w-64">
              <img
                src={currentTrack.artwork_url}
                alt={currentTrack.title}
                className="w-12 h-12 object-cover rounded"
              />
              <div className="min-w-0">
                <h4 className="text-sm font-medium text-white truncate" data-testid="player-track-title">
                  {currentTrack.title}
                </h4>
                <p className="text-xs text-slate-400 truncate">
                  {currentTrack.artist_name}
                </p>
              </div>
            </div>

            {/* Playback Controls */}
            <div className="flex-1 max-w-xl hidden md:flex flex-col items-center gap-2">
              <div className="flex items-center gap-4">
                <button
                  className="text-slate-400 hover:text-white transition-colors"
                  data-testid="player-prev"
                >
                  <SkipBack className="w-5 h-5" />
                </button>
                <button
                  onClick={togglePlay}
                  className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center hover:bg-emerald-400 hover:scale-105 transition-all"
                  data-testid="player-play-pause"
                >
                  {isPlaying ? (
                    <Pause className="w-5 h-5 text-slate-900" />
                  ) : (
                    <Play className="w-5 h-5 text-slate-900 ml-0.5" />
                  )}
                </button>
                <button
                  className="text-slate-400 hover:text-white transition-colors"
                  data-testid="player-next"
                >
                  <SkipForward className="w-5 h-5" />
                </button>
              </div>

              {/* Progress Bar */}
              <div className="flex items-center gap-2 w-full">
                <span className="text-xs text-slate-500 w-10 text-right">
                  {formatTime(progress)}
                </span>
                <Slider
                  value={[progress]}
                  max={duration || 180}
                  step={1}
                  onValueChange={([value]) => seek(value)}
                  className="flex-1"
                  data-testid="player-progress"
                />
                <span className="text-xs text-slate-500 w-10">
                  {formatTime(duration || 180)}
                </span>
              </div>
            </div>

            {/* Volume & License CTA */}
            <div className="flex items-center gap-4 md:w-64 justify-end">
              {/* Mobile Play Button */}
              <button
                onClick={togglePlay}
                className="md:hidden w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center"
                data-testid="player-play-pause-mobile"
              >
                {isPlaying ? (
                  <Pause className="w-5 h-5 text-slate-900" />
                ) : (
                  <Play className="w-5 h-5 text-slate-900 ml-0.5" />
                )}
              </button>

              {/* Volume */}
              <div className="hidden md:flex items-center gap-2">
                <button
                  onClick={() => setVolume(volume > 0 ? 0 : 0.8)}
                  className="text-slate-400 hover:text-white transition-colors"
                  data-testid="player-volume-toggle"
                >
                  {volume > 0 ? (
                    <Volume2 className="w-5 h-5" />
                  ) : (
                    <VolumeX className="w-5 h-5" />
                  )}
                </button>
                <Slider
                  value={[volume * 100]}
                  max={100}
                  step={1}
                  onValueChange={([value]) => setVolume(value / 100)}
                  className="w-24"
                  data-testid="player-volume"
                />
              </div>

              {/* License CTA */}
              <Button
                onClick={() => navigate(`/tracks/${currentTrack.id}`)}
                className="bg-emerald-500 text-slate-900 hover:bg-emerald-400 font-semibold text-sm px-4 rounded-lg"
                data-testid="player-license-cta"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">License</span>
              </Button>

              {/* Close player */}
              <button
                onClick={closePlayer}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                data-testid="player-close"
                aria-label="Close player"
                title="Close player"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
